import _ from 'underscore';

import { I9nSchemaBySystem } from '@biteinc/common';
import {
  AutoAddScannableItemsMode,
  AutoAddScannableItemsModeHelper,
  CustomerAccountsUsage,
  CustomerAccountsUsageHelper,
  DuplicateItemMergeMode,
  DuplicateItemMergeModeHelper,
  EpsonBarcodeFormat,
  EpsonBarcodeFormatHelper,
  ExpoPrinterMode,
  GroupOrderingMode,
  GroupOrderingNameType,
  GuestReceiptTextSize,
  I9nSyncScope,
  I9nSyncScopeHelper,
  ItemCardMultiPriceOptionDisplay,
  ItemCardPriceOptionDisplayHelper,
  KioskGuestReceiptMode,
  KioskOrderingMode,
  KioskOrderingModeHelper,
  KioskReducedHeightSize,
  KioskReducedHeightSizeHelper,
  LanguageCode,
  LanguageCodeHelper,
  LeadTimeSource,
  LeadTimeSourceHelper,
  LocationRecommendationsLevel,
  LocationRecommendationsLevelHelper,
  LoyaltyInMenuMode,
  LoyaltyInMenuModeHelper,
  LoyaltyInPaymentOptionsMode,
  LoyaltyInPaymentOptionsModeHelper,
  LoyaltyPostAuthStep,
  LoyaltySignupMethod,
  LoyaltySignupMethodHelper,
  LoyaltySignupMode,
  LoyaltySignupModeHelper,
  ManagerCodeForScannedItemsMode,
  ManagerCodeForScannedItemsModeHelper,
  MenuItemCategory,
  MenuItemCategoryHelper,
  MenuItemDisplayPricePrecalculation,
  MenuItemViewDetailsStyle,
  MenuItemViewDetailsStyleHelper,
  MenuModifierPriceDisplayStyle,
  MenuModifierPriceDisplayStyleHelper,
  MenuNavigationViewType,
  MenuNavigationViewTypeHelper,
  MenuPriceStyle,
  MenuPriceStyleHelper,
  MenuSectionHeaderType,
  MenuSectionHeaderTypeHelper,
  ModelType,
  OrderUpdateToastsMode,
  OrderUpdateToastsModeHelper,
  OrderValidationMode,
  PaymentTerminalStatusRecoveryStep,
  QuickAddEligibility,
  QuickAddEligibilityHelper,
  QuickAddSimpleItemsMode,
  QuickAddSimpleItemsModeHelper,
  RecommendationsFirstLoadVisibility,
  RecommendationsFirstLoadVisibilityHelper,
  RewardPointsDisplayMode,
  RewardPointsDisplayModeHelper,
  SecretMenuType,
  SideNavImageSize,
  SimpleLoyaltySignupMode,
  SimpleLoyaltySignupModeHelper,
} from '@biteinc/enums';
import { StringHelper, Time } from '@biteinc/helpers';
import { settingsSchema } from '@biteinc/schemas';

app.Settings = app.AbstractModel.extend({
  ModelName: 'settings',
  Schema: settingsSchema,
  Type: ModelType.Settings,

  canBeDestroyed() {
    return false;
  },

  getFieldCollection(field, subProperty) {
    const fieldName = (subProperty ? `${subProperty}.` : '') + field;
    switch (fieldName) {
      case 'posAutomaticSyncScope':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: I9nSyncScope,
          nameGenerator: I9nSyncScopeHelper.name,
        });
      case 'alwaysDisplayModGroupMagicCopyKeysInCart':
        if (!app.modGroupOptionList) {
          app.modGroupOptionList = new app.ModGroupOptionList();
          // Org-level lists cover every location's mod groups and can outlive the default 30s timeout.
          app.modGroupOptionList.fetch({ reset: true, timeout: Time.MINUTE });
        }
        return app.modGroupOptionList;
      case 'defaultMenuSectionHeaderType':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuSectionHeaderType,
          nameGenerator: MenuSectionHeaderTypeHelper.name,
        });
      case 'expoPrinterMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: ExpoPrinterMode,
          nameGenerator: (expoPrinterMode) => {
            switch (expoPrinterMode) {
              case ExpoPrinterMode.PrintAllOrders:
                return 'Print All Orders';
              case ExpoPrinterMode.PrintOnlyOnDemandOrders:
                return 'Print Only On-Demand Orders';
              case ExpoPrinterMode.PrintOnlySentOrders:
                return 'Print Only Sent Orders';
            }
          },
        });
      case 'kioskGuestReceiptMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: KioskGuestReceiptMode,
          nameGenerator: (kioskGuestReceiptMode) => {
            switch (kioskGuestReceiptMode) {
              case KioskGuestReceiptMode.PrintAllOrders:
                return 'Print All Orders';
              case KioskGuestReceiptMode.PrintOnlyCashOrders:
                return 'Print Only Cash Orders';
              case KioskGuestReceiptMode.PrintOnlyOnDemandOrders:
                return 'Print Only On-Demand Orders';
            }
          },
        });
      case 'kioskOrderingMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: KioskOrderingMode,
          nameGenerator: KioskOrderingModeHelper.name,
        });
      case 'autoAddScannableItemsMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: AutoAddScannableItemsMode,
          nameGenerator: AutoAddScannableItemsModeHelper.name,
          disclaimerGenerator: (mode) => {
            switch (mode) {
              case AutoAddScannableItemsMode.No:
                return '';
              case AutoAddScannableItemsMode.PricedItems:
                return 'Only barcoded items that have a price become scannable.';
              case AutoAddScannableItemsMode.AllItems:
                return 'Barcoded items become scannable even when they are $0.';
              default:
                throw new Error(`unrecognized autoAddScannableItemsMode value: ${mode}`);
            }
          },
          // Least to most permissive, rather than the default alphabetical sort.
          sort: app.AbstractCollection.SortOptions.AS_IS,
        });
      case 'managerCodeForScannedItemsMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: ManagerCodeForScannedItemsMode,
          nameGenerator: ManagerCodeForScannedItemsModeHelper.name,
          disclaimerGenerator: (mode) => {
            switch (mode) {
              case ManagerCodeForScannedItemsMode.Never:
                return 'Guests can remove scanned items and exit the menu on their own.';
              case ManagerCodeForScannedItemsMode.Removal:
                return 'Only removing a scanned item asks for an employee access code.';
              case ManagerCodeForScannedItemsMode.RemovalAndMenuExit:
                return 'Removing a scanned item and exiting the menu with scanned items both ask for an employee access code.';
              default:
                throw new Error(`unrecognized managerCodeForScannedItemsMode value: ${mode}`);
            }
          },
          // Least to most restrictive, rather than the default alphabetical sort.
          sort: app.AbstractCollection.SortOptions.AS_IS,
        });
      case 'showRewardPointsAs':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: RewardPointsDisplayMode,
          nameGenerator: RewardPointsDisplayModeHelper.name,
          disclaimerGenerator: (mode) => {
            switch (mode) {
              case RewardPointsDisplayMode.Hide:
                return 'The point cost of a reward appears nowhere.';
              case RewardPointsDisplayMode.ShowInLabel:
                return 'The point cost follows the reward name, e.g. "Free Drink (500 points)".';
              case RewardPointsDisplayMode.ShowAsPill:
                return 'The point cost shows in a colored pill under the reward name.';
              default:
                throw new Error(`unrecognized showRewardPointsAs value: ${mode}`);
            }
          },
          sort: app.AbstractCollection.SortOptions.AS_IS,
        });
      case 'leadTimeSource':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LeadTimeSource,
          nameGenerator: LeadTimeSourceHelper.name,
        });
      case 'orderUpdateToasts':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: OrderUpdateToastsMode,
          nameGenerator: OrderUpdateToastsModeHelper.name,
          sort: app.AbstractCollection.SortOptions.AS_IS,
        });
      case 'duplicateItemMergeMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: DuplicateItemMergeMode,
          nameGenerator: DuplicateItemMergeModeHelper.name,
          sort: app.AbstractCollection.SortOptions.AS_IS,
        });
      case 'supportedLanguages':
      case 'maintenanceLanguages':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LanguageCode,
          nameGenerator: LanguageCodeHelper.name,
        });
      case 'recommendationsFirstLoadVisibility':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: RecommendationsFirstLoadVisibility,
          nameGenerator: RecommendationsFirstLoadVisibilityHelper.name,
          values: RecommendationsFirstLoadVisibilityHelper.recommendationsFirstLoadVisibilityValues,
        });
      case 'recommendationsInMenuVisibility':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: RecommendationsFirstLoadVisibility,
          nameGenerator: RecommendationsFirstLoadVisibilityHelper.name,
          values: RecommendationsFirstLoadVisibilityHelper.recommendationsInMenuVisibilityValues,
        });
      case 'recommendationsLevel':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LocationRecommendationsLevel,
          nameGenerator: LocationRecommendationsLevelHelper.name,
        });
      case 'loyaltySignup.simpleMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: SimpleLoyaltySignupMode,
          nameGenerator: SimpleLoyaltySignupModeHelper.name,
        });
      case 'customerAccountsUsage':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: CustomerAccountsUsage,
          nameGenerator: CustomerAccountsUsageHelper.name,
        });
      case 'priceStyle':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuPriceStyle,
          nameGenerator: MenuPriceStyleHelper.name,
        });
      case 'modifierPriceDisplayStyle':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuModifierPriceDisplayStyle,
          nameGenerator: MenuModifierPriceDisplayStyleHelper.name,
        });
      case 'loyaltyInMenuMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LoyaltyInMenuMode,
          nameGenerator: LoyaltyInMenuModeHelper.name,
        });
      case 'loyaltyPostAuthSteps':
      case 'loyaltySignup.postAuthSteps':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LoyaltyPostAuthStep,
          nameGenerator: (step) => {
            switch (step) {
              case LoyaltyPostAuthStep.Exit:
                return 'Exit';
              case LoyaltyPostAuthStep.Rewards:
                return 'Rewards';
              case LoyaltyPostAuthStep.OrderHistory:
                return 'Order History';
            }
          },
        });
      case 'loyaltySignup.mode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LoyaltySignupMode,
          nameGenerator: LoyaltySignupModeHelper.name,
        });
      case 'reducedHeightSize':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: KioskReducedHeightSize,
          nameGenerator: KioskReducedHeightSizeHelper.name,
        });
      case 'loyaltySignup.loyaltySignupMethods':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LoyaltySignupMethod,
          nameGenerator: LoyaltySignupMethodHelper.name,
        });
      case 'loyaltyInPaymentOptionsMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LoyaltyInPaymentOptionsMode,
          nameGenerator: LoyaltyInPaymentOptionsModeHelper.name,
        });
      case 'orderNumberBarcodeFormat':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: EpsonBarcodeFormat,
          nameGenerator: EpsonBarcodeFormatHelper.name,
        });
      case 'vendorInstructionsSize':
      case 'guestReceiptBodyTextSize':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: GuestReceiptTextSize,
          // Large blows up glyph width and wraps receipt lines; the guest
          // receipt body only offers Small (compact font) and Medium (the
          // printer's standard font).
          ...(fieldName === 'guestReceiptBodyTextSize' && {
            values: [GuestReceiptTextSize.Small, GuestReceiptTextSize.Medium],
          }),
          nameGenerator: (size) => {
            switch (size) {
              case GuestReceiptTextSize.Small:
                return 'Small';
              case GuestReceiptTextSize.Medium:
                return 'Medium';
              case GuestReceiptTextSize.Large:
                return 'Large';
            }
          },
        });
      case 'printQRCodeWithPosOrderID':
        const fieldSet = new Set();
        const allI9nSchemas = _.chain(app.locationList.models)
          .map((location) => {
            return location.getAllI9nSchemas();
          })
          .flatten()
          .value();

        allI9nSchemas.forEach((integration) => {
          const schema = I9nSchemaBySystem[integration.system];
          if (!schema) {
            return;
          }
          if (schema.type !== 'pos') {
            return;
          }
          if (schema.posCheckIdKey) {
            fieldSet.add(schema.posCheckIdKey);
          }
          schema.searchableFieldKeys?.forEach((key) => fieldSet.add(key));
        });
        const values = Array.from(fieldSet);
        return app.AbstractCollection.createFromArray({
          values,
          nameGenerator: (value) => {
            return value;
          },
        });
      case 'precalculateMenuItemDisplayPrice':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuItemDisplayPricePrecalculation,
          nameGenerator: (style) => {
            switch (style) {
              case MenuItemDisplayPricePrecalculation.Off:
                return 'Off';
              case MenuItemDisplayPricePrecalculation.IncludeDefaultMods:
                return 'Include default mods';
              case MenuItemDisplayPricePrecalculation.IncludeCheapestModsOnRequiredGroups:
                return 'Include cheapest mod on required groups';
            }
          },
        });
      case 'menuNavigation.sideNav.imageSize':
      case 'sideNav.imageSize':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: SideNavImageSize,
          nameGenerator: (sideNavImageSize) => sideNavImageSize,
        });
      case 'secretMenu.columnCount': {
        const { min, max } = settingsSchema.fields.secretMenu.fields.columnCount;
        return app.AbstractCollection.createFromIntValues(min, max);
      }
      case 'secretMenu.type':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: SecretMenuType,
          nameGenerator: (secretMenuType) => {
            switch (secretMenuType) {
              case SecretMenuType.SideNav:
                return 'Side Nav';
            }
          },
        });
      case 'quickAdd.eligibility':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: QuickAddEligibility,
          nameGenerator: QuickAddEligibilityHelper.name,
        });
      case 'quickAdd.simpleItemsMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: QuickAddSimpleItemsMode,
          nameGenerator: QuickAddSimpleItemsModeHelper.name,
          disclaimerGenerator: (mode) => {
            switch (mode) {
              case QuickAddSimpleItemsMode.ShowViewButton:
                return "Shows the 'View' and 'Add' buttons on cards for items with no customizations.";
              case QuickAddSimpleItemsMode.HideViewButton:
                return "Hides the on-card 'View' button when the item has no customizations.";
              case QuickAddSimpleItemsMode.AddToCart:
                return 'Tapping the card adds the item directly to the cart. Also governs scanner-only card taps.';
              case QuickAddSimpleItemsMode.AddToCartWithQuantity:
                return 'Tapping the card adds the item directly to the cart, and the on-card button becomes a quantity selector.';
              default:
                throw new Error(`unrecognized quickAdd.simpleItemsMode value: ${mode}`);
            }
          },
          // Least to most aggressive, rather than the default alphabetical sort.
          sort: app.AbstractCollection.SortOptions.AS_IS,
        });
      case 'groupOrderingMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: GroupOrderingMode,
          nameGenerator: (groupOrderingMode) => {
            switch (groupOrderingMode) {
              case GroupOrderingMode.Off:
                return 'Off';
              case GroupOrderingMode.AskGroupOrderRequired:
                return 'Ask Group Order Required';
              case GroupOrderingMode.AskGroupOrderOptional:
                return 'Ask Group Order Optional';
            }
          },
        });
      case 'groupOrderingNameType':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: GroupOrderingNameType,
          nameGenerator: (groupOrderingNameType) => {
            switch (groupOrderingNameType) {
              case GroupOrderingNameType.Name:
                return 'Guest Names';
              case GroupOrderingNameType.Number:
                return 'Number (auto)';
            }
          },
        });
      case 'pageNavigationSettings.columnCount':
        return app.AbstractCollection.createFromIntValues(2, 3);
      case 'pageNavigationSettings.displayType':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuSectionHeaderType,
          nameGenerator: MenuSectionHeaderTypeHelper.name,
        });
      case 'pageNavigationSettings.size':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: {
            small: 'small',
            medium: 'medium',
            large: 'large',
          },
          nameGenerator: (size) => size.charAt(0).toUpperCase() + size.slice(1),
        });
      case 'itemCardMultiPriceOptionDisplay':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: ItemCardMultiPriceOptionDisplay,
          nameGenerator: (display) => ItemCardPriceOptionDisplayHelper.name(display),
        });
      case 'skipIfCartOnlyContainsCategories':
      case 'skipDiningOptionIfCartOnlyContainsCategories':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuItemCategory,
          nameGenerator: MenuItemCategoryHelper.name,
        });
      case 'orderValidationMode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: OrderValidationMode,
          nameGenerator: (mode) => StringHelper.toTitleCase(mode.replaceAll('-', ' ')),
        });
      case 'paymentTerminalStatusRecoveryStep':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: PaymentTerminalStatusRecoveryStep,
          nameGenerator: (value) => StringHelper.toTitleCase(value.replaceAll('-', ' ')),
        });
      case 'menuNavigation.type':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuNavigationViewType,
          nameGenerator: MenuNavigationViewTypeHelper.name,
        });
      case 'menuItemViewDetailsStyle':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuItemViewDetailsStyle,
          nameGenerator: MenuItemViewDetailsStyleHelper.name,
        });
      default:
        return null;
    }
  },

  detailsViewClassForListField(/* field, subProperty, keyModel */) {
    return null;
  },

  overridesField(fieldName) {
    return this.get('overrideSet').includes(fieldName);
  },

  url() {
    return `/api/v2/settings/${this.id}`;
  },
});
